/** @file Builds the export bundle of a project: validate, pack, resolve, composite, write files, manifest. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { SECTOR_TABLES } from '@zilo/pose3d-format/tables/sectorTables';
import type { Clip } from '@zilo/pose3d-format/types/clip';
import type { Warning } from '@zilo/pose3d-format/types/warning';
import { validateClip } from '@zilo/pose3d-format/validate/validateClip';
import { validateClipAgainstPuppet } from '@zilo/pose3d-format/validate/validateClipAgainstPuppet';
import { NAME_PATTERN } from '@zilo/pose3d-format/validate/fields/namePattern';
import { validatePuppet } from '@zilo/pose3d-format/validate/validatePuppet';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { bundleFiles } from '@zilo/pose3d-export/files/bundleFiles';
import { canonicalJson } from '@zilo/pose3d-export/hash/canonicalJson';
import { buildManifest } from '@zilo/pose3d-export/manifest/buildManifest';
import { collectBlocks } from '@zilo/pose3d-export/pack/collectBlocks';
import { packBlocks } from '@zilo/pose3d-export/pack/packBlocks';
import { resolvePuppet } from '@zilo/pose3d-export/resolve/resolvePuppet';
import { bundleSheets } from '@zilo/pose3d-export/sheets/bundleSheets';
import type { ExportBundle } from '@zilo/pose3d-export/types/bundle';
import type { ExportDeps, ExportInput } from '@zilo/pose3d-export/types/exportDeps';

/**
 * The bundle of `input` (async: image-io's `encodePng` is, R5). `validatePuppet` and `validateClip` errors throw;
 * clip nodes the puppet lacks warn (`UNKNOWN_NODE_IN_CLIP` at `/clips/<name>/...`) and the clip is kept; empty slots
 * warn (`EMPTY_SLOT`). `files` holds every bundle file, `manifest.json` included.
 */
export async function buildBundle(
  input: ExportInput, deps: ExportDeps,
): Promise<{ bundle: ExportBundle; files: Record<string, Uint8Array | string>; warnings: Warning[] }> {
  const puppet = validatePuppet(input.puppet, deps.profiles === undefined ? {} : { profiles: deps.profiles });
  const warnings: Warning[] = [];
  const clips: Record<string, Clip> = {};
  for (const [name, doc] of Object.entries(input.clips)) {
    if (!NAME_PATTERN.test(name)) {
      throwError(ERROR_CODES.VALIDATION, { clip: name }, { path: `/clips/${name.replace(/~/g, '~0').replace(/\//g, '~1')}`, hint: 'clip names are file names: letters, digits, _ . -' });
    }
    const clip = validateClip(doc);
    warnings.push(...validateClipAgainstPuppet(clip, puppet).map((w) => ({ ...w, path: `/clips/${name}${w.path ?? ''}` })));
    clips[name] = JSON.parse(JSON.stringify(clip)) as Clip;
  }
  const blocks = collectBlocks(puppet, deps.sectorTables ?? SECTOR_TABLES);
  const packed = packBlocks(blocks, deps);
  const resolved = resolvePuppet(puppet, deps, packed);
  warnings.push(...resolved.warnings);
  const sheets = bundleSheets(puppet, resolved.puppet, blocks, packed, deps);
  const files = await bundleFiles({ puppet: resolved.puppet, clips, sheets }, deps);
  const manifest = buildManifest(input, resolved.puppet.profileData, resolved.puppet.sheets, files, deps);
  files['manifest.json'] = canonicalJson(manifest);
  return { bundle: { manifest, puppet: resolved.puppet, clips, sheets }, files, warnings };
}
